import { Button, Checkbox, CircularProgress, Pagination, Stack } from '@mui/material'
import React, { useState } from 'react'
import Swal from 'sweetalert2'
import { useRestoreChannelProductsMutation } from '../../../hooks/RTK_Service/MapUnmapProductData'

export default function DeletedTable({ DeletedProductData, pageNumber, handlePageNumber, refreshData }) {
  const [restoreChannelProducts, { isLoading: restoring }] = useRestoreChannelProductsMutation()
  const [restoringId, setRestoringId] = useState(null)
  const [selectedIds, setSelectedIds] = useState([])

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(DeletedProductData?.content?.map((d) => d.id) ?? [])
    } else {
      setSelectedIds([])
    }
  }

  const handleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }

  const allSelected =
    DeletedProductData?.content?.length > 0 &&
    DeletedProductData.content.every((d) => selectedIds.includes(d.id))

  const handleRestore = async (id) => {
    const confirm = await Swal.fire({
      title: 'Restore Product?',
      text: 'This product will be moved back to the unmapped products list.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Restore',
    })
    if (!confirm.isConfirmed) return
    setRestoringId(id)
    try {
      const res = await restoreChannelProducts([id])
      if (res?.data?.status === 0) {
        Swal.fire('Restored!', res.data.message, 'success')
        setSelectedIds((prev) => prev.filter((x) => x !== id))
        refreshData && refreshData()
      } else {
        Swal.fire('Error', res?.error?.data?.message ?? 'Restore failed.', 'error')
      }
    } catch {
      Swal.fire('Error', 'An unexpected error occurred.', 'error')
    } finally {
      setRestoringId(null)
    }
  }

  const handleBatchRestore = async () => {
    if (selectedIds.length === 0) {
      Swal.fire('Warning', 'No products selected.', 'warning')
      return
    }
    const confirm = await Swal.fire({
      title: `Restore ${selectedIds.length} product(s)?`,
      text: 'Selected products will be moved back to the unmapped products list.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Restore All',
    })
    if (!confirm.isConfirmed) return
    try {
      const res = await restoreChannelProducts(selectedIds)
      if (res?.data?.status === 0) {
        Swal.fire('Restored!', res.data.message, 'success')
        setSelectedIds([])
        refreshData && refreshData()
      } else {
        Swal.fire('Error', res?.error?.data?.message ?? 'Restore failed.', 'error')
      }
    } catch {
      Swal.fire('Error', 'An unexpected error occurred.', 'error')
    }
  }

  return (
    <div>
      {selectedIds.length > 0 && (
        <div className="mb-2 d-flex align-items-center gap-2">
          <span className="text-muted">{selectedIds.length} selected</span>
          <Button
            variant="contained"
            color="success"
            size="small"
            disabled={restoring}
            onClick={handleBatchRestore}
          >
            {restoring ? <CircularProgress size={16} color="inherit" /> : 'Restore Selected'}
          </Button>
        </div>
      )}

      <div className="table-responsive">
        <table className="table table-hover mt-2">
          <thead style={{ borderBottom: '1px solid black' }}>
            <tr>
              <th scope="col">
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={selectedIds.length > 0 && !allSelected}
                  onChange={handleSelectAll}
                />
              </th>
              <th scope="col">CHANNEL ID</th>
              <th scope="col">CHANNEL SKU</th>
              <th scope="col">CHANNEL PRODUCT ID</th>
              <th scope="col">CHANNEL PRODUCT TITLE</th>
              <th scope="col">SKU</th>
              <th scope="col">CHANNEL PRICE</th>
              <th scope="col">CHANNEL SALE PRICE</th>
              <th scope="col">RESTORE</th>
            </tr>
          </thead>
          <tbody>
            {DeletedProductData?.content?.length === 0 && (
              <tr>
                <td colSpan={9} className="text-center text-muted py-3">
                  No deleted products found.
                </td>
              </tr>
            )}
            {DeletedProductData?.content?.map((data) => (
              <tr key={'deleted' + data.id}>
                <td>
                  <Checkbox
                    size="small"
                    checked={selectedIds.includes(data.id)}
                    onChange={() => handleSelectOne(data.id)}
                  />
                </td>
                <td>{data?.channelId}</td>
                <td>{data.channelSku}</td>
                <td>{data.channelProductId}</td>
                <td>{data.channelProductTitle}</td>
                <td>{data.sku ?? <span className="text-muted">—</span>}</td>
                <td>{data.channelPrice}</td>
                <td>{data.channelSalePrice}</td>
                <td>
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    disabled={restoringId === data.id}
                    onClick={() => handleRestore(data.id)}
                  >
                    {restoringId === data.id ? <CircularProgress size={16} /> : 'Restore'}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="right">
          <Stack spacing={2}>
            <Pagination
              color="primary"
              count={DeletedProductData?.totalPages ?? 1}
              page={pageNumber}
              onChange={(e, v) => handlePageNumber(v)}
            />
          </Stack>
        </div>
      </div>
    </div>
  )
}
